import React, { useState, useCallback, useEffect } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { cropImageWithArea, type CropArea } from '../../../utils/imageCompressor';
import { useLocalization, type Language } from '../../../config/localization';

export interface CropViewState {
  crop: { x: number; y: number };
  zoom: number;
}

export interface CropAdjustmentPanelProps {
  originalUrl: string;
  targetWidth: number;
  targetHeight: number;
  convertToJpeg?: boolean;
  /** Restore the crop position/zoom from a previous edit so reopening preserves it. */
  initialView?: CropViewState;
  onApply: (
    croppedFile: File,
    blobUrl: string,
    dimensions: { width: number; height: number },
    view: CropViewState
  ) => void;
  onCancel: () => void;
  language: Language;
}

/**
 * Inner crop editor (header → cropper → footer), without portal/backdrop/card chrome.
 * Rendered by both `CropAdjustmentModal` (standalone) and `ImageEditModal` (tabbed shell).
 */
export default function CropAdjustmentPanel({
  originalUrl,
  targetWidth,
  targetHeight,
  convertToJpeg,
  initialView,
  onApply,
  onCancel,
  language,
}: CropAdjustmentPanelProps) {
  const t = useLocalization(language);
  const cropModalT = t.widgets?.slotsInputMedia?.cropModal;

  const aspect = targetWidth / targetHeight;

  const [crop, setCrop] = useState<Point>(initialView?.crop ?? { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialView?.zoom ?? 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPx: Area) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const onCropAreaChange = useCallback((_croppedArea: Area, croppedAreaPx: Area) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleResetCenter = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels) return;

    setApplying(true);
    setError(false);
    try {
      const cropArea: CropArea = {
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
      };

      // Fetch the original image as a File from the blob URL
      const response = await fetch(originalUrl);
      const blob = await response.blob();
      const file = new File([blob], 'crop-source.png', { type: blob.type });

      const result = await cropImageWithArea(file, cropArea, targetWidth, targetHeight, convertToJpeg);
      const blobUrl = URL.createObjectURL(result.file);

      onApply(result.file, blobUrl, result.finalDimensions, { crop, zoom });
    } catch (err) {
      console.error('Crop apply failed:', err);
      setError(true);
    } finally {
      setApplying(false);
    }
  }, [croppedAreaPixels, originalUrl, targetWidth, targetHeight, convertToJpeg, onApply, crop, zoom]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !applying) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [applying, onCancel]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700">
        <h2 className="text-white text-lg font-semibold">{cropModalT?.title || 'Adjust Crop'}</h2>
        <span className="text-gray-400 text-sm">
          {targetWidth} x {targetHeight}
        </span>
      </div>

      {/* Crop area */}
      <div className="relative flex-1 min-h-[400px] bg-gray-950">
        <Cropper
          image={originalUrl}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          restrictPosition={true}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          onCropAreaChange={onCropAreaChange}
          style={{ containerStyle: { background: '#0a0a0f' } }}
        />
      </div>

      {/* Bottom bar: zoom slider + buttons */}
      <div className="px-5 py-3 border-t border-gray-700 flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <label className="text-gray-400 text-sm whitespace-nowrap">{cropModalT?.zoom || 'Zoom'}</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
          <span className="text-gray-400 text-sm w-12 text-right">{Math.round(zoom * 100)}%</span>
        </div>

        {error && <span className="text-red-400 text-sm">{cropModalT?.error || 'Failed to apply crop'}</span>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetCenter}
            disabled={applying}
            className="px-3 py-1.5 text-sm text-gray-300 border border-gray-600 rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {cropModalT?.resetCenter || 'Reset to Center'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={applying}
            className="px-3 py-1.5 text-sm text-gray-300 border border-gray-600 rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {cropModalT?.cancel || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={applying || !croppedAreaPixels}
            className="px-4 py-1.5 text-sm text-white bg-purple-600 rounded hover:bg-purple-500 transition-colors disabled:opacity-50"
          >
            {applying ? '...' : cropModalT?.apply || 'Apply'}
          </button>
        </div>
      </div>
    </>
  );
}
